import React from "react";
import {
  getGuestCount,
  getCustomerName,
  formatTimeForLocale
} from "../../../../../utils/booking";
import {
  getBookingAssignedAreaNamesAsString,
  getBookingDurationAsString,
  getBookingEndTimeAsString,
  getBookingStatus,
  getBookingsZoneNamesAsString,
  getBookingTimeAsString,
  getBookingType
} from "../../../../../utils/singleBooking";
import { User, Clock, MapPin, Hash, Phone } from "lucide-react";
import { EDSBadge } from "../../../../eds/EDSBadge";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useUserContext } from "../../../../../hooks/useUserContext";

const SelectedBookingHeader = () => {
  const { user } = useUserContext();
  const { selectedBooking, venueConfig } = useDiaryContext();
  if (!selectedBooking || !venueConfig) {
    return null;
  }

  const customerName = getCustomerName(selectedBooking);
  const guestCount = getGuestCount([selectedBooking]);
  const bookingStatus = getBookingStatus(selectedBooking);
  const bookingType = getBookingType({ selectedBooking, venueConfig });
  const bookingTimeAsString = formatTimeForLocale(
    getBookingTimeAsString(selectedBooking),
    user?.locale
  );
  const bookingEndTimeAsString = formatTimeForLocale(
    getBookingEndTimeAsString(selectedBooking),
    user?.locale
  );
  const bookingDurationAsString = getBookingDurationAsString(selectedBooking);
  const bookingAssignedAreasAsString =
    getBookingAssignedAreaNamesAsString(selectedBooking);
  const bookingsZoneNamesAsString = getBookingsZoneNamesAsString({
    selectedBooking,
    venueConfig
  });

  return (
    <div className="CustomerDetailsHeader mx-panel-padding mt-panel-padding">
      <div className="Customer flex items-center text-collins-heading">
        <div className="userCount flex items-center text-collins-primary font-semibold text-muted-foreground">
          <User
            className="UserIcon h-[18px] w-[18px] mr-1.5 text-current shrink-0 mt-px"
            size={16}
          />
          <span className="guestCount font-bold text-foreground leading-none text-[16px]">
            {guestCount}
          </span>
        </div>
        <span className="UserName ml-3 text-collins-heading font-bold text-foreground">
          {customerName}
        </span>
      </div>
      <div className="Pills mt-panel-padding flex gap-2 flex-wrap">
        <EDSBadge
          variant="default"
          className="booking-status-badge"
          style={
            {
              "--booking-type-color": bookingStatus.colour,
              "--booking-type-foreground": "var(--black)"
            } as React.CSSProperties
          }
        >
          <span className="booking-status-badge-text">
            {bookingStatus.label}
          </span>
        </EDSBadge>
        {bookingType && (
          <EDSBadge
            variant="default"
            className="booking-type-badge"
            style={
              {
                "--booking-type-color": bookingType.color ?? "#e5e7eb",
                "--booking-type-foreground": "var(--black)"
              } as React.CSSProperties
            }
          >
            <span className="booking-type-badge-text">{bookingType.name}</span>
          </EDSBadge>
        )}
      </div>
      <div className="BookingInfo flex items-center gap-4 mt-panel-padding flex-wrap">
        <div className="BookingInfoItem flex items-center gap-1.5 shrink-0">
          <Clock
            className="BookingInfoIcon text-muted-foreground shrink-0"
            size={14}
          />
          <div className="BookingInfoContent flex items-center gap-1">
            <span className="BookingInfoPrimary text-[var(--fontSize-m)] font-medium text-foreground whitespace-nowrap">
              {bookingTimeAsString}
              {bookingTimeAsString !== bookingEndTimeAsString
                ? ` - ${bookingEndTimeAsString}`
                : ""}
            </span>
            {bookingDurationAsString && (
              <span className="BookingInfoSecondary text-collins-secondary text-muted-foreground whitespace-nowrap">
                {bookingDurationAsString}
              </span>
            )}
          </div>
        </div>

        <div className="BookingInfoItem flex items-center gap-1.5 shrink-0">
          <MapPin
            className="BookingInfoIcon text-muted-foreground shrink-0"
            size={14}
          />
          <div className="BookingInfoContent flex items-center gap-1">
            <span className="BookingInfoPrimary text-[var(--fontSize-m)] font-medium text-foreground whitespace-nowrap">
              {bookingAssignedAreasAsString}
            </span>
            {bookingsZoneNamesAsString && (
              <span className="BookingInfoSecondary text-collins-secondary text-muted-foreground whitespace-nowrap">
                {bookingsZoneNamesAsString}
              </span>
            )}
          </div>
        </div>

        <div className="BookingInfoItem flex items-center gap-1.5 shrink-0">
          <Hash
            className="BookingInfoIcon text-muted-foreground shrink-0"
            size={14}
          />
          <div className="BookingInfoContent flex items-center gap-1">
            <span className="BookingInfoPrimary text-[var(--fontSize-m)] font-medium text-foreground whitespace-nowrap">
              DMN-{selectedBooking?.reference}
            </span>
          </div>
        </div>

        {selectedBooking?.phone && (
          <div className="BookingInfoItem flex items-center gap-1.5 shrink-0">
            <Phone
              className="BookingInfoIcon text-muted-foreground shrink-0"
              size={14}
            />
            <div className="BookingInfoContent flex items-center gap-1">
              <a
                href={`tel:${selectedBooking.phone}`}
                className="PhoneLink no-underline text-inherit hover:no-underline"
              >
                <span className="BookingInfoPrimary text-[var(--fontSize-m)] font-medium text-foreground whitespace-nowrap">
                  {selectedBooking.phone}
                </span>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectedBookingHeader;
